import React, { useState } from 'react';
import {
  PhotoIcon,
  CommandLineIcon,
  CpuChipIcon,
  CircleStackIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  PlayIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import API_BASE_URL from '../Config/config';

/**
 * Universal Evidence Submission Component
 *
 * Handles all evidence types based on AI-determined requirements:
 * - Installation/Setup → Screenshot + Terminal Output
 * - Coding → Built-in Compiler
 * - Projects → GitHub Repository
 * - Concepts → Adaptive Questions
 * - SQL → Script + Query Output
 * - Architecture → Diagram Upload
 * - Theory → AI Conversation
 */
const EvidenceSubmission = ({
  evidenceType,
  onEvidenceChange,
  githubInfo,
  onRefreshGitHub,
  userId,
  enrollmentId,
  taskId,
}) => {
  const [compilerOpen, setCompilerOpen] = useState(false);
  const [code, setCode] = useState('');
  const [codeLanguage, setCodeLanguage] = useState('python');
  const [codeOutput, setCodeOutput] = useState(null);
  const [compiling, setCompiling] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});

  // Unified Multi-field States
  const [sqlScript, setSqlScript] = useState('');
  const [queryOutput, setQueryOutput] = useState('');
  const [commands, setCommands] = useState('');
  const [terminalOutput, setTerminalOutput] = useState('');
  const [comments, setComments] = useState('');
  const [githubUrl, setGithubUrl] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);

  if (!evidenceType) {
    return (
      <div className="p-4 bg-secondary/10 rounded-xl border border-secondary/20 text-center text-muted text-sm">
        Loading evidence requirements...
      </div>
    );
  }

  const handleFileUpload = async (field, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        onEvidenceChange(field, reader.result);
        setUploadingFile(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('File upload failed:', err);
      setUploadingFile(false);
    }
  };

  const handleRunCode = async () => {
    if (!code.trim()) {
      setCodeOutput({ error: 'Please write some code first.' });
      return;
    }

    setCompiling(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = { 
        'Content-Type': 'application/json', 
        'user-id': userId
      };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        headers['X-Session-Token'] = sessionToken;
      }
      const res = await fetch(`${API_BASE_URL}/api/compiler/execute?userId=${encodeURIComponent(userId)}&user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ language: codeLanguage, code, input: '' }),
      });
      const data = await res.json();
      setCodeOutput(data);

      // Store code in evidence
      onEvidenceChange('code', code);
      onEvidenceChange('language', codeLanguage);
    } catch (err) {
      setCodeOutput({ error: 'Compiler service unavailable' });
    } finally {
      setCompiling(false);
    }
  };

  const fetchAdaptiveQuestions = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = { 
        'Content-Type': 'application/json', 
        'user-id': userId
      };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        headers['X-Session-Token'] = sessionToken;
      }
      const res = await fetch(`${API_BASE_URL}/api/ai/adaptive-questions?userId=${encodeURIComponent(userId)}&user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ enrollmentId, taskId }),
      });
      const data = await res.json();
      if (data.ok && data.questions) {
        setQuestions(data.questions);
      }
    } catch (err) {
      console.error('Failed to fetch questions:', err);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    const updated = { ...answers, [questionId]: answer };
    setAnswers(updated);
    onEvidenceChange('answers', updated);
  };

  const handleVideoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      alert("Video file size exceeds 50MB limit.");
      return;
    }

    setVideoFile(file);
    const url = URL.createObjectURL(file);
    setVideoPreview(url);
    
    // In a real app, you'd upload this to S3 and get a URL
    // For now, we'll just pass the file object or a placeholder
    onEvidenceChange('videoUrl', url);
  };

  return (
    <div className="space-y-4">
      {/* AI Guidance Header */}
      <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="text-2xl bg-white p-2 rounded-full shadow-sm">🤖</div>
          <div className="flex-1 pt-1">
            <h4 className="text-sm font-bold text-primary mb-1">AI Mentor Guidance</h4>
            <p className="text-sm text-zinc-700">{evidenceType.instructions}</p>
            {evidenceType.requirements && (
              <div className="mt-3 flex flex-wrap gap-2">
                {evidenceType.requirements.map((req, i) => (
                  <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-white text-zinc-600 font-medium border border-zinc-200 shadow-sm flex items-center gap-1">
                    {req.icon} {req.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Screenshot Evidence */}
      {evidenceType.fields?.includes('screenshot') && (
        <div className="p-3 rounded-xl bg-secondary/5 border border-secondary/20 space-y-2">
          <label className="text-xs font-bold text-gray-200 flex items-center gap-2">
            <PhotoIcon className="h-4 w-4 text-primary" />
            Upload Screenshot
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileUpload('screenshotUrl', e)}
            disabled={uploadingFile}
            className="w-full text-xs text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/90"
          />
          {uploadingFile && <p className="text-xs text-muted">Uploading...</p>}
        </div>
      )}

      {/* Terminal Output Evidence */}
      {(evidenceType.fields?.includes('terminal_output') || evidenceType.fields?.includes('terminalOutput')) && (
        <div className="p-3 rounded-xl bg-secondary/5 border border-secondary/20 space-y-2">
          <label className="text-xs font-bold text-gray-200 flex items-center gap-2">
            <CommandLineIcon className="h-4 w-4 text-primary" />
            Terminal/CLI Output
          </label>
          <textarea
            value={terminalOutput}
            onChange={(e) => {
              setTerminalOutput(e.target.value);
              onEvidenceChange('terminalOutput', e.target.value);
            }}
            className="w-full bg-bgMain border border-secondary/30 rounded-lg p-3 text-xs text-gray-200 font-mono focus:outline-none focus:border-primary resize-none"
            placeholder="Paste your terminal output here..."
            rows={6}
          />
        </div>
      )}

      {/* Built-in Compiler Evidence */}
      {evidenceType.fields?.includes('built_in_compiler') && (
        <div className="p-3 rounded-xl bg-secondary/5 border border-secondary/20 space-y-3">
          <button
            onClick={() => setCompilerOpen(!compilerOpen)}
            className="w-full py-2.5 bg-primary/10 text-primary rounded-lg text-sm font-bold border border-primary/30 hover:bg-primary/20 flex items-center justify-center gap-2 transition-all"
          >
            <CpuChipIcon className="h-5 w-5" />
            {compilerOpen ? 'Close' : 'Open'} Built-in Compiler
          </button>

          {compilerOpen && (
            <div className="space-y-3 p-3 bg-bgMain rounded-xl border border-secondary/30">
              {/* Language Selector */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted">Language:</label>
                <select
                  value={codeLanguage}
                  onChange={(e) => setCodeLanguage(e.target.value)}
                  className="bg-secondary/20 border border-secondary/30 rounded-lg px-3 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-primary"
                >
                  <option value="python">Python</option>
                  <option value="javascript">JavaScript</option>
                  <option value="java">Java</option>
                  <option value="cpp">C++</option>
                  <option value="c">C</option>
                  <option value="go">Go</option>
                </select>
              </div>

              {/* Code Editor */}
              <textarea
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full bg-black/50 border border-secondary/30 rounded-lg p-3 text-xs text-gray-200 font-mono focus:outline-none focus:border-primary resize-none"
                placeholder={`// Write your ${codeLanguage} code here...\n\nfunction solution() {\n  // Your implementation\n}`}
                rows={12}
              />

              {/* Run Button */}
              <button
                onClick={handleRunCode}
                disabled={compiling || !code.trim()}
                className="w-full py-2.5 bg-primary text-white rounded-lg text-xs font-bold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {compiling ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4" />
                    Run Code
                  </>
                )}
              </button>

              {/* Output */}
              {codeOutput && (
                <div className="p-3 rounded-lg bg-black/70 border border-secondary/30 space-y-2">
                  <div className="text-xs font-bold text-primary">Output:</div>
                  <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap">
                    {codeOutput.error || codeOutput.output || codeOutput.stdout || 'No output'}
                  </pre>
                  {codeOutput.stderr && (
                    <pre className="text-xs text-red-400 font-mono whitespace-pre-wrap mt-2">
                      {codeOutput.stderr}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* SQL Script / Query Output Evidence */}
      {(evidenceType.fields?.includes('sql_script') || evidenceType.fields?.includes('query_output') || evidenceType.fields?.includes('commands')) && (
        <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/20 space-y-4">
          <label className="text-xs font-bold text-blue-600 flex items-center gap-2">
            <CircleStackIcon className="h-4 w-4" />
            Database Evidence
          </label>
          
          {(evidenceType.fields?.includes('sql_script') || evidenceType.fields?.includes('sqlScript')) && (
            <div className="space-y-1">
              <span className="text-[10px] text-muted">SQL / Database Script</span>
              <textarea
                value={sqlScript}
                onChange={(e) => {
                  setSqlScript(e.target.value);
                  onEvidenceChange('sqlScript', e.target.value);
                }}
                className="w-full bg-bgMain border border-blue-500/30 rounded-lg p-3 text-xs text-gray-200 font-mono focus:outline-none focus:border-blue-500 resize-none"
                placeholder="CREATE TABLE... INSERT INTO..."
                rows={4}
              />
            </div>
          )}

          {(evidenceType.fields?.includes('query_output') || evidenceType.fields?.includes('queryOutput')) && (
            <div className="space-y-1">
              <span className="text-[10px] text-muted">Query Results</span>
              <textarea
                value={queryOutput}
                onChange={(e) => {
                  setQueryOutput(e.target.value);
                  onEvidenceChange('queryOutput', e.target.value);
                }}
                className="w-full bg-bgMain border border-blue-500/30 rounded-lg p-3 text-xs text-gray-200 font-mono focus:outline-none focus:border-blue-500 resize-none"
                placeholder="Rows: 10, Execution time: 2ms..."
                rows={3}
              />
            </div>
          )}

          {evidenceType.fields?.includes('commands') && (
            <div className="space-y-1">
              <span className="text-[10px] text-muted">MongoDB / Shell Commands</span>
              <textarea
                value={commands}
                onChange={(e) => {
                  setCommands(e.target.value);
                  onEvidenceChange('commands', e.target.value);
                }}
                className="w-full bg-bgMain border border-blue-500/30 rounded-lg p-3 text-xs text-gray-200 font-mono focus:outline-none focus:border-blue-500 resize-none"
                placeholder="db.collection.find()..."
                rows={3}
              />
            </div>
          )}
        </div>
      )}

      {/* GitHub Repository Evidence */}
      {(evidenceType.fields?.includes('github_repo') || evidenceType.types?.includes('githubUrl')) && (
        <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 space-y-3">
          {!githubInfo || (githubInfo.provisioning_status === 'PENDING' && !githubInfo.github_repo_url) ? (
            <div className="flex flex-col items-center justify-center py-4 space-y-2">
              <ArrowPathIcon className="h-6 w-6 text-primary animate-spin" />
              <p className="text-xs text-muted">Your GitHub repository is being provisioned...</p>
              <button 
                onClick={onRefreshGitHub}
                className="text-[10px] text-primary hover:underline font-bold bg-primary/10 px-2 py-1 rounded"
              >
                Check Status
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CommandLineIcon className="h-5 w-5 text-primary" />
                  <span className="text-sm font-bold text-gray-200">GitHub Repository</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={onRefreshGitHub}
                    className="text-[10px] font-bold text-primary hover:underline flex items-center gap-1 bg-primary/10 px-2 py-1 rounded"
                  >
                    <ArrowPathIcon className="h-3 w-3" />
                    Refresh
                  </button>
                  {githubInfo.github_repo_url && (
                    <a
                      href={githubInfo.github_repo_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] font-bold bg-primary text-white px-2 py-1 rounded hover:bg-primary/90 flex items-center gap-1"
                    >
                      <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                      Open Repo
                    </a>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-[10px] bg-bgMain/40 p-2 rounded-lg">
                <div>
                  <span className="text-muted block uppercase text-[8px] tracking-widest">Username</span>
                  <p className="text-gray-300 font-mono truncate">{githubInfo.github_username || 'Not set'}</p>
                </div>
                <div>
                  <span className="text-muted block uppercase text-[8px] tracking-widest">Status</span>
                  <p
                    className={`font-bold ${
                      githubInfo.provisioning_status === 'READY' ? 'text-green-500' : 'text-blue-500'
                    }`}
                  >
                    {githubInfo.provisioning_status || 'PENDING'}
                  </p>
                </div>
                {githubInfo.provisioning_status === 'READY' && (
                  <>
                    <div>
                      <span className="text-muted block uppercase text-[8px] tracking-widest">Commits</span>
                      <p className="text-gray-300">{githubInfo.commit_count || 0}</p>
                    </div>
                    <div>
                      <span className="text-muted block uppercase text-[8px] tracking-widest">Last Push</span>
                      <p className="text-gray-300">
                        {githubInfo.last_push_at
                          ? new Date(githubInfo.last_push_at).toLocaleDateString()
                          : 'None'}
                      </p>
                    </div>
                  </>
                )}
              </div>

              {githubInfo.invitation_status !== 'ACCEPTED' && (
                <p className="text-[9px] text-yellow-500 font-medium bg-yellow-500/10 p-1 rounded text-center animate-pulse">
                  ⚠ Accept the GitHub invitation to enable automatic reviews
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* SQL Evidence */}
      {evidenceType.fields?.includes('sqlScript') && (
        <div className="space-y-3">
          <div className="p-3 rounded-xl bg-secondary/5 border border-secondary/20 space-y-2">
            <label className="text-xs font-bold text-gray-200 flex items-center gap-2">
              <CircleStackIcon className="h-4 w-4 text-primary" />
              SQL Script
            </label>
            <textarea
              onChange={(e) => onEvidenceChange('sqlScript', e.target.value)}
              className="w-full bg-bgMain border border-secondary/30 rounded-lg p-3 text-xs text-gray-200 font-mono focus:outline-none focus:border-primary resize-none"
              placeholder="-- Paste your SQL script here\nSELECT * FROM table;"
              rows={6}
            />
          </div>

          <div className="p-3 rounded-xl bg-secondary/5 border border-secondary/20 space-y-2">
            <label className="text-xs font-bold text-gray-200">Query Output</label>
            <textarea
              onChange={(e) => onEvidenceChange('queryOutput', e.target.value)}
              className="w-full bg-bgMain border border-secondary/30 rounded-lg p-3 text-xs text-gray-200 font-mono focus:outline-none focus:border-primary resize-none"
              placeholder="Paste the query result here..."
              rows={4}
            />
          </div>
        </div>
      )}

      {/* Architecture Diagram Evidence */}
      {evidenceType.fields?.includes('architectureDiagram') && (
        <div className="p-3 rounded-xl bg-secondary/5 border border-secondary/20 space-y-2">
          <label className="text-xs font-bold text-gray-200 flex items-center gap-2">
            <DocumentTextIcon className="h-4 w-4 text-primary" />
            Upload Architecture Diagram
          </label>
          <input
            type="file"
            accept="image/*,.pdf"
            onChange={(e) => handleFileUpload('architectureDiagram', e)}
            disabled={uploadingFile}
            className="w-full text-xs text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/90"
          />
        </div>
      )}

      {/* Adaptive Questions Evidence */}
      {evidenceType.fields?.includes('adaptive_questions') && (
        <div className="p-3 rounded-xl bg-secondary/5 border border-secondary/20 space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-bold text-gray-200 flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary" />
              Adaptive Questions
            </label>
            {questions.length === 0 && (
              <button
                onClick={fetchAdaptiveQuestions}
                className="text-xs font-bold bg-primary text-white px-3 py-1.5 rounded-lg hover:bg-primary/90"
              >
                Start Questions
              </button>
            )}
          </div>

          {questions.length > 0 && (
            <div className="space-y-4">
              {questions.map((q, idx) => (
                <div key={idx} className="p-3 bg-bgMain rounded-lg border border-secondary/30 space-y-2">
                  <p className="text-xs font-bold text-gray-200">
                    Question {idx + 1}: {q.question}
                  </p>
                  <textarea
                    value={answers[q.id] || ''}
                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                    className="w-full bg-secondary/10 border border-secondary/30 rounded-lg p-2 text-xs text-gray-200 focus:outline-none focus:border-primary resize-none"
                    placeholder="Type your answer here..."
                    rows={3}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Additional Comments */}
      <div className="p-3 rounded-xl bg-secondary/5 border border-secondary/20 space-y-2">
        <label className="text-xs font-bold text-gray-200 flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="h-4 w-4 text-primary" />
          Additional Comments
        </label>
        <textarea
          value={additionalComments}
          onChange={(e) => {
            setAdditionalComments(e.target.value);
            onEvidenceChange('additionalComments', e.target.value);
          }}
          className="w-full bg-bgMain border border-secondary/30 rounded-lg p-3 text-xs text-gray-200 focus:outline-none focus:border-primary resize-none"
          placeholder="Anything else you'd like the AI Mentor to know?"
          rows={3}
        />
      </div>

      {/* Optional Video Upload */}
      <div className="p-3 rounded-xl bg-secondary/5 border border-secondary/20 space-y-2">
        <label className="text-xs font-bold text-gray-200 flex items-center gap-2">
          <PlayIcon className="h-4 w-4 text-primary" />
          Optional Video Walkthrough
        </label>
        <input
          type="file"
          accept="video/*"
          onChange={handleVideoUpload}
          className="w-full text-xs text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/90"
        />
        {videoPreview && (
          <div className="mt-2 rounded-lg overflow-hidden border border-secondary/30 aspect-video">
            <video src={videoPreview} controls className="w-full h-full object-cover" />
          </div>
        )}
      </div>
    </div>
  );
};

export default EvidenceSubmission;
